import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import { useState, useEffect } from 'react';
import { getCookie } from './utils/cookies';
import { initTenant, getSubdomain } from './services/tenant';
import Layout from './components/Layout';
import RequireRoute from './components/RequireRoute';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Resources from './pages/Resources';
import ProxyResources from './components/ProxyResources';
import SSHResources from './pages/SSHResources';
import K8sResources from './pages/K8sResources';
import AccessRequests from './pages/AccessRequests';
import Sessions from './pages/Sessions';
import UsersPage from './pages/Users';
import GroupsPage from './pages/Groups';
import RolesPage from './pages/Roles';
import UserDetailPage from './pages/UserDetailPage';
import GroupDetailPage from './pages/GroupDetailPage';
import RoleDetailPage from './pages/RoleDetailPage';
import SSHActivityLogs from './pages/SSHActivityLogs';
import AccessLogs from './pages/AccessLogs';
import Settings from './pages/Settings';
import Certificates from './pages/Certificates';
import K8sTerminalPage from './pages/K8sTerminalPage';
import SSHTerminalPage from './pages/SSHTerminalPage';
import TrustedClusters from './pages/TrustedClusters';
import DesktopResources from './pages/DesktopResources';
import DatabaseResources from './pages/DatabaseResources';
import AWSResources from './pages/AWSResources';
import AzureResources from './pages/AzureResources';
import HetznerResources from './pages/HetznerResources';
import GCPResources from './pages/GCPResources';
import CloudProviders from './pages/CloudProviders';
import Policies from './pages/Policies';
import SetupGuide from './pages/SetupGuide';
import DesktopSession from './pages/DesktopSession';
import SessionPlayerPage from './pages/SessionPlayerPage';
import SessionJoinPage from './pages/SessionJoinPage';
import { UserProvider } from './contexts/UserContext';
import { TourProvider } from './tours/TourContext';
import { Spinner } from '@/components/ds-zta';
import { createQueryClient } from '@/lib/queryClient';

// A failed mutation with no onError of its own toasts the server's message;
// see lib/queryClient.ts for the opt-out (meta: { silentError: true }).
const queryClient = createQueryClient();

// TenantUnavailable renders when the subdomain does not map to a known tenant, or
// the tenant-availability check failed. The tenant is derived from the URL, so this
// usually means the subdomain is wrong or the backend is unreachable.
function TenantUnavailable({ status }: { status: 'unavailable' | 'error' }) {
  const subdomain = getSubdomain();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--d2d-bg-primary)] p-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-medium text-[var(--d2d-text-primary)] mb-2">
          {status === 'unavailable' ? 'Unknown tenant' : 'Unable to reach the service'}
        </h1>
        <p className="text-[var(--d2d-text-secondary)]">
          {status === 'unavailable'
            ? <>No workspace is configured for <span className="font-mono font-medium">{subdomain}</span>. Check the URL and try again.</>
            : <>We couldn't verify this workspace right now. Please retry shortly.</>}
        </p>
      </div>
    </div>
  );
}

// isSharedCallbackLanding reports whether this page load is the shared d2auth
// callback — the single fixed URL every tenant is sent back to. That host serves
// no tenant of its own, so the usual tenant gate below cannot run: the tenant is
// recovered on the server from `state` (see pkg/api/pkce.go) and the browser is
// forwarded to the tenant's own origin from there.
//
// Matching on a "/callback" suffix covers both shapes registered with d2auth —
// <base>/callback (the shared callback) and <base>/auth/callback (the older
// per-tenant ones) — so the registered URL can change without a rebuild.
//
// Only the code/error landing needs the bypass. The ?claim= landing already sits
// on the tenant's own origin, where the tenant resolves normally.
function isSharedCallbackLanding(): boolean {
  const onCallbackRoute = window.location.pathname.replace(/\/$/, '').endsWith('/callback');
  if (!onCallbackRoute) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tenantStatus, setTenantStatus] = useState<'loading' | 'ok' | 'unavailable' | 'error'>('loading');

  useEffect(() => {
    // Resolve the tenant from the URL and verify it exists before rendering the
    // app — every API call must carry the X-Tenant-Id header, set once resolved.
    let cancelled = false;
    initTenant()
      .then((r) => { if (!cancelled) setTenantStatus(r.available ? 'ok' : 'unavailable'); })
      .catch(() => { if (!cancelled) setTenantStatus('error'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Check authentication status on mount
    const checkAuth = () => {
      const token = getCookie('auth_token');
      setIsAuthenticated(token !== null);
      setIsLoading(false);
    };

    checkAuth();

    // Check periodically for cookie changes
    const interval = setInterval(checkAuth, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // The shared callback lands before any tenant is known — render the callback
  // page on its own so it can hand the login on to the right tenant.
  if (isSharedCallbackLanding()) {
    return (
      <QueryClientProvider client={queryClient}>
        <Router basename={import.meta.env.BASE_URL}>
          <Routes>
            <Route path="*" element={<AuthCallback />} />
          </Routes>
        </Router>
      </QueryClientProvider>
    );
  }

  if (tenantStatus === 'loading' || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--d2d-bg-primary)]">
        {/* Tenant and session are being resolved before any route exists, so
            there is no layout to put a skeleton in: a full-screen spinner. */}
        <Spinner size={48} label="Loading" className="text-[var(--d2d-text-primary)]" />
      </div>
    );
  }

  if (tenantStatus !== 'ok') {
    return <TenantUnavailable status={tenantStatus} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename={import.meta.env.BASE_URL}>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Both callback shapes registered with d2auth: <base>/callback is the
              shared one, <base>/auth/callback the older per-tenant ones. */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route path="/terminal" element={isAuthenticated ? <K8sTerminalPage /> : <Navigate to="/login" replace />} />
          <Route path="/ssh-terminal" element={isAuthenticated ? <SSHTerminalPage /> : <Navigate to="/login" replace />} />
          {/* Full-screen like the terminals (design eval F42): inside the shell the
              remote screen overflowed the viewport by 104px. */}
          <Route path="/desktop-session/:id" element={isAuthenticated ? <DesktopSession /> : <Navigate to="/login" replace />} />
          <Route
            path="/*"
            element={
              isAuthenticated ? (
                <UserProvider>
                  <TourProvider>
                    <Layout>
                      <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/resources" element={<RequireRoute path="/resources"><Resources /></RequireRoute>} />
                      <Route path="/proxy-resources" element={<ProxyResources />} />
                      <Route path="/ssh-resources" element={<SSHResources />} />
                      <Route path="/k8s-resources" element={<K8sResources />} />
                      <Route path="/databases" element={<DatabaseResources />} />
                      <Route path="/ssh-activity-logs" element={<SSHActivityLogs />} />
                      <Route path="/access-requests" element={<AccessRequests />} />
                      <Route path="/sessions" element={<RequireRoute path="/sessions"><Sessions /></RequireRoute>} />
                      <Route path="/users" element={<UsersPage />} />
                      <Route path="/users/:id" element={<UserDetailPage />} />
                      <Route path="/groups" element={<GroupsPage />} />
                      <Route path="/groups/:id" element={<GroupDetailPage />} />
                      <Route path="/roles" element={<RolesPage />} />
                      <Route path="/roles/:id" element={<RoleDetailPage />} />
                      {/* Guarded routes use the nav's own check (RequireRoute, design eval F27);
                          the backend must enforce the same rules. */}
                      <Route path="/access-logs" element={<RequireRoute path="/access-logs"><AccessLogs /></RequireRoute>} />
                      <Route path="/certificates" element={<Certificates />} />
                      <Route path="/settings" element={<RequireRoute path="/settings"><Settings /></RequireRoute>} />
                      <Route path="/trusted-clusters" element={<RequireRoute path="/trusted-clusters"><TrustedClusters /></RequireRoute>} />
                      <Route path="/desktop-resources" element={<DesktopResources />} />
                      <Route path="/cloud-providers" element={<CloudProviders />} />
                      <Route path="/cloud-providers/aws" element={<AWSResources />} />
                      <Route path="/cloud-providers/azure" element={<AzureResources />} />
                      <Route path="/cloud-providers/hetzner" element={<HetznerResources />} />
                      <Route path="/cloud-providers/gcp" element={<GCPResources />} />
                      <Route path="/aws-resources" element={<Navigate to="/cloud-providers/aws" replace />} />
                      <Route path="/sessions/:id/player" element={<RequireRoute path="/sessions/player"><SessionPlayerPage /></RequireRoute>} />
                      <Route path="/sessions/:id/join" element={<RequireRoute path="/sessions/join"><SessionJoinPage /></RequireRoute>} />
                      <Route path="/policies" element={<Policies />} />
                      <Route path="/setup-guide" element={<RequireRoute path="/setup-guide"><SetupGuide /></RequireRoute>} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                    </Layout>
                  </TourProvider>
                </UserProvider>
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
